/**
 * Directory search for people and Spaces.
 * Default tab is Spaces (paginated directory). People tab shows a random sample
 * when empty, or identity search hits when querying.
 */

import { useState, useEffect, useCallback, lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PublicIdentity } from '@adieuu/shared';
import { useDirectorySearch } from '../hooks/useDirectorySearch';
import { useDirectoryRecentSearches } from '../hooks/useDirectoryRecentSearches';
import { usePlatformSearchSuggestions } from '../hooks/usePlatformSearchSuggestions';
import { useIdentity } from '../hooks/useIdentity';
import { AliasSignInPrompt } from '../components/AliasSignInPrompt';
import { DirectoryRecentSearches } from '../components/DirectoryRecentSearches';
import { IdentityCard } from '../components/IdentityCard';
import { Input } from '../components/Input';
import { Tabs, TabList, TabTrigger, TabContent, type TabItem } from '../components/Tabs';
import { Icon } from '../icons/Icon';
import { useDebouncedDirectoryRecentPersist } from '../hooks/useDebouncedDirectoryRecentPersist';
import { SearchSpacesDirectory } from './spaces/SearchSpacesDirectory';
import '../styles/_spaces.scss';

function normalizeDirectoryQuery(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

type SearchTab = 'people' | 'spaces';

function tabFromParams(params: URLSearchParams): SearchTab {
  return params.get('tab') === 'people' ? 'people' : 'spaces';
}

const SearchPeopleWithFriends = lazy(() =>
  import('./SearchPeopleWithFriends').then((m) => ({ default: m.SearchPeopleWithFriends })),
);

function SearchPeopleGuest({ people }: { people: PublicIdentity[] }) {
  const [signInOpen, setSignInOpen] = useState(false);

  return (
    <>
      <div className="search-results-grid">
        {people.map((result) => (
          <IdentityCard
            key={result.id}
            identity={result}
            showFriendAction
            onRequireAlias={() => setSignInOpen(true)}
          />
        ))}
      </div>
      <AliasSignInPrompt
        open={signInOpen}
        onOpenChange={setSignInOpen}
        bodyKey="search.aliasRequiredFriend"
      />
    </>
  );
}

export function Search() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { identity, status: identityStatus } = useIdentity();
  const isIdentityLoggedIn = identityStatus === 'logged_in' && identity;

  const [inputValue, setInputValue] = useState(() => searchParams.get('q') ?? '');
  const [activeTab, setActiveTab] = useState<SearchTab>(() => tabFromParams(searchParams));
  const { recents, add: addRecent, remove: removeRecent, clear: clearRecents } =
    useDirectoryRecentSearches();
  const {
    people,
    discoverPeople,
    isSearching,
    isDiscoverLoading,
    searchError,
    discoverError,
    search,
    query,
  } = useDirectorySearch({
    debounceMs: 300,
    searchLimit: 50,
    enableSpaces: false,
  });

  const writeParams = useCallback(
    (next: { q?: string; tab: SearchTab }) => {
      const params: Record<string, string> = { tab: next.tab };
      if (next.q) params.q = next.q;
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const applyQuery = useCallback(
    (raw: string, { persist }: { persist?: boolean } = {}) => {
      setInputValue(raw);
      const normalized = normalizeDirectoryQuery(raw);
      search(raw);
      writeParams({ q: normalized || undefined, tab: activeTab });
      if (persist && normalized.length >= 2) {
        addRecent(normalized);
      }
    },
    [search, writeParams, activeTab, addRecent],
  );

  // Sync from the URL without clobbering in-progress typing that only differs
  // by trailing whitespace (which we strip from `q` for a stable URL).
  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    setActiveTab(tabFromParams(searchParams));
    setInputValue((current) => {
      if (current === q || normalizeDirectoryQuery(current) === q) {
        return current;
      }
      return q;
    });
    search(q);
  }, [searchParams, search]);

  useDebouncedDirectoryRecentPersist(inputValue, addRecent);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applyQuery(e.target.value);
    },
    [applyQuery],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (normalizeDirectoryQuery(inputValue)) {
        applyQuery(inputValue, { persist: true });
      }
    },
    [inputValue, applyQuery],
  );

  const handleRecentClick = useCallback(
    (recent: string) => {
      applyQuery(recent, { persist: true });
    },
    [applyQuery],
  );

  const handleClear = useCallback(() => {
    applyQuery('');
  }, [applyQuery]);

  const handleTabChange = useCallback(
    (tab: string) => {
      const next: SearchTab = tab === 'people' ? 'people' : 'spaces';
      setActiveTab(next);
      const normalized = normalizeDirectoryQuery(inputValue);
      writeParams({ q: normalized || undefined, tab: next });
    },
    [inputValue, writeParams],
  );

  const isQueryActive = query.length >= 2;
  const showDiscover = !query;
  const { mode: platformMode, terms: platformTerms } = usePlatformSearchSuggestions(showDiscover);
  const platformLabelKey =
    platformMode === 'trending' ? 'search.platformTrending' : 'search.platformRecent';
  const peopleList = isQueryActive ? people : discoverPeople;
  const peopleLoading = isQueryActive ? isSearching : isDiscoverLoading;
  const peopleError = isQueryActive ? searchError : discoverError;

  const peopleTabLabel = isQueryActive
    ? t('search.sections.people')
    : t('search.discoverPeople');
  const spacesTabLabel = t('search.sections.spaces');

  const discoverHint =
    activeTab === 'spaces' ? t('search.spacesDirectoryHint') : t('search.discoverHint');

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        value: 'people',
        label: isQueryActive ? `${peopleTabLabel} (${peopleList.length})` : peopleTabLabel,
      },
      {
        value: 'spaces',
        label: spacesTabLabel,
      },
    ],
    [isQueryActive, peopleTabLabel, spacesTabLabel, peopleList.length],
  );

  const renderPeople = (list: PublicIdentity[]) => {
    if (list.length === 0) {
      return (
        <div className="search-empty">
          <p>{t('search.noResults')}</p>
          <p className="search-empty-hint">{t('search.noResultsHint')}</p>
        </div>
      );
    }
    if (isIdentityLoggedIn) {
      return (
        <Suspense
          fallback={
            <div className="search-loading">
              <div className="spinner spinner-lg" />
            </div>
          }
        >
          <SearchPeopleWithFriends people={list} selfIdentityId={identity.id} />
        </Suspense>
      );
    }
    return <SearchPeopleGuest people={list} />;
  };

  return (
    <div className="page-content">
      <div className="container">
        <header className="page-header">
          <h1 className="page-title">
            {isQueryActive ? t('search.title') : t('search.discoverTitle')}
          </h1>
          <p className="page-subtitle">
            {isQueryActive ? t('search.subtitle') : t('search.discoverSubtitle')}
          </p>
        </header>

        <form onSubmit={handleSubmit} className="search-form">
          <Input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={t('search.placeholder')}
            leftIcon={<Icon name="search" />}
            rightIcon={
              inputValue ? (
                <button
                  type="button"
                  className="input-password-toggle"
                  onClick={handleClear}
                  aria-label={t('search.clear')}
                >
                  <Icon name="x" />
                </button>
              ) : undefined
            }
            inputSize="lg"
            autoFocus
          />
        </form>

        <div className="search-results">
          {showDiscover && (
            <div className="search-discover-intro">
              <p className="search-hint">{discoverHint}</p>
              <DirectoryRecentSearches
                recents={recents}
                onSelect={handleRecentClick}
                onRemove={removeRecent}
                onClear={clearRecents}
              />
              <DirectoryRecentSearches
                recents={platformTerms}
                onSelect={handleRecentClick}
                labelKey={platformLabelKey}
                editable={false}
              />
            </div>
          )}
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="search-tabs"
          >
            <TabList mobileItems={tabItems}>
              <TabTrigger value="people">
                {peopleTabLabel}
                {isQueryActive ? (
                  <span className="search-tab-count">{peopleList.length}</span>
                ) : null}
              </TabTrigger>
              <TabTrigger value="spaces">{spacesTabLabel}</TabTrigger>
            </TabList>
            <TabContent value="people" className="search-tab-panel">
              {peopleLoading ? (
                <div className="search-loading">
                  <div className="spinner spinner-lg" />
                </div>
              ) : peopleError ? (
                <div className="search-error">
                  <p>{peopleError}</p>
                </div>
              ) : (
                renderPeople(peopleList)
              )}
            </TabContent>
            <TabContent value="spaces" className="search-tab-panel">
              <SearchSpacesDirectory query={isQueryActive ? query : ''} />
            </TabContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
